import { qualityScore } from '../../utils/evaluation-rubric';
import {
  runPassed,
  type ExpectedAgent,
  type StartPosition,
  type ValidateRun,
} from './validate-runs';

/**
 * Test suites: a named set of scenarios run together, the way the testing
 * tool runs them (`apps/tester`: a `TestConfig` of `suiteConfig` +
 * `scenarios[]`, expanded into scenarios × iterations and run through
 * `processWithConcurrency`).
 *
 * Here a case is one simulated scenario — the same thing New scenario runs —
 * and every case a suite runs lands in the runs list as a normal scenario
 * run, tagged with the suite. Pure, so expansion, the concurrency runner and
 * the rollups are tested without a page.
 */

export interface SuiteCase {
  id: string;
  name: string;
  /** What happens in the conversation — the simulated customer's goal. */
  description: string;
  /** What the solution should do; what the run is scored against. */
  expectation?: string;
  expectedAgent?: ExpectedAgent | null;
  startAt: StartPosition;
  /**
   * Customer messages at most — for the whole conversation, or for each
   * `ai:` step of a script.
   */
  maxTurns: number;
  /** Written-out steps (`case-script.ts`); empty means the AI customer plays it. */
  script?: string;
  /** Text the replies must contain, one per line in the editor. */
  mustSay?: string[];
  /** Text the replies must never contain. */
  mustNotSay?: string[];
}

/**
 * The page and customer every conversation in the suite reports, the way a
 * customer's page would: its URL (and so its query parameters), a
 * passthrough, and who they are. All optional.
 */
export interface SuiteContext {
  pageUrl?: string;
  /** JSON, or plain text sent as-is. */
  passthrough?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

export interface ValidateSuite {
  id: string;
  name: string;
  /** The channel every case runs on; null until one is picked. */
  channelAppId: string | null;
  /** How many times each case runs — the tester's `iterations`. */
  iterations: number;
  /** How many cases run at once — the tester's `parallel`, capped lower here. */
  concurrency: number;
  cases: SuiteCase[];
  context?: SuiteContext;
  /**
   * AI Gateway models to compare: every case runs once per model (× runs per
   * case). Empty runs the solution's own model.
   */
  models?: string[];
  createdAt: number;
  updatedAt: number;
}

export type SuiteRunStatus = 'running' | 'done' | 'stopped';

/** One run of a suite: which case runs it produced, and how it ended. */
export interface SuiteRun {
  id: string;
  suiteId: string;
  /** The suite's name then, so a later rename doesn't rewrite history. */
  suiteName: string;
  channelName: string;
  startedAt: number;
  endedAt: number | null;
  status: SuiteRunStatus;
  /** Cases × iterations. */
  total: number;
  /** Runs recorded so far, in the order they finished. */
  runIds: string[];
  /** Why the scheduler ended a run early (a spent model-call budget). */
  stoppedBecause?: string;
}

export const DEFAULT_MAX_TURNS = 6;
export const MAX_TURNS_LIMIT = 20;
export const DEFAULT_CONCURRENCY = 2;
/** The tester's own caps (`apps/tester` `SuiteConfigSchema`: parallel 1–10, iterations 1–50). */
export const MAX_CONCURRENCY = 10;
export const MAX_ITERATIONS = 50;
export const MAX_MODELS = 6;

const clampInt = (
  value: unknown,
  { min, max, fallback }: { min: number; max: number; fallback: number }
) => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
};

export const clampConcurrency = (value: unknown) =>
  clampInt(value, {
    min: 1,
    max: MAX_CONCURRENCY,
    fallback: DEFAULT_CONCURRENCY,
  });
export const clampIterations = (value: unknown) =>
  clampInt(value, { min: 1, max: MAX_ITERATIONS, fallback: 1 });
export const clampMaxTurns = (value: unknown) =>
  clampInt(value, {
    min: 1,
    max: MAX_TURNS_LIMIT,
    fallback: DEFAULT_MAX_TURNS,
  });

/* ---- Building suites ---------------------------------------------------- */

export const newCase = (
  id: string,
  over: Partial<SuiteCase> = {}
): SuiteCase => ({
  id,
  name: '',
  description: '',
  startAt: 'beginning',
  maxTurns: DEFAULT_MAX_TURNS,
  ...over,
});

export const newSuite = (
  id: string,
  now: number,
  over: Partial<ValidateSuite> = {}
): ValidateSuite => ({
  id,
  name: 'Untitled suite',
  channelAppId: null,
  iterations: 1,
  concurrency: DEFAULT_CONCURRENCY,
  cases: [],
  createdAt: now,
  updatedAt: now,
  ...over,
});

/** A scenario run, saved as a case: same goal, expectation, agent and start. */
export const caseFromRun = (id: string, run: ValidateRun): SuiteCase =>
  newCase(id, {
    name: run.title.length > 60 ? `${run.title.slice(0, 57)}…` : run.title,
    description: run.title,
    expectation: run.expectation,
    expectedAgent: run.expectedAgent ?? null,
    startAt: run.startAt ?? 'beginning',
  });

/** A copy with fresh ids, named "… (copy)". */
export const duplicateSuite = (
  suite: ValidateSuite,
  makeId: () => string,
  now: number
): ValidateSuite => ({
  ...suite,
  id: makeId(),
  name: `${suite.name} (copy)`,
  cases: suite.cases.map((c) => ({ ...c, id: makeId() })),
  createdAt: now,
  updatedAt: now,
});

/** A case runs from its script, or — without one — from what happens. */
export const caseIsScripted = (c: SuiteCase) => !!c.script?.trim();

/** A case can run once it says what happens, or has a script. */
export const caseIsRunnable = (c: SuiteCase) =>
  c.description.trim().length > 0 || caseIsScripted(c);

/** Why a suite can't run yet, or null when it can. */
export const suiteBlocker = (
  suite: ValidateSuite,
  channelExists: (appId: string) => boolean
): string | null => {
  if (!suite.channelAppId || !channelExists(suite.channelAppId)) {
    return 'Pick a channel to run on.';
  }
  if (!suite.cases.length) return 'Add a case first.';
  if (!suite.cases.some(caseIsRunnable)) {
    return 'Every case needs “What happens in the conversation?” or a script.';
  }
  return null;
};

/* ---- Running ------------------------------------------------------------ */

export interface SuiteTask {
  case: SuiteCase;
  /** 1-based. */
  iteration: number;
  /** The model this run compares, when the suite compares models. */
  model?: string;
}

/** The suite's models to compare, cleaned: trimmed, unique, at most six. */
export const suiteModels = (suite: ValidateSuite): string[] =>
  [...new Set((suite.models ?? []).map((m) => m.trim()).filter(Boolean))].slice(
    0,
    MAX_MODELS
  );

/**
 * Cases × models × iterations, case by case — skipping cases with nothing to
 * run. Models run side by side for a case, so a comparison fills in evenly.
 */
export const expandSuite = (suite: ValidateSuite): SuiteTask[] => {
  const iterations = clampIterations(suite.iterations);
  const models = suiteModels(suite);
  return suite.cases
    .filter(caseIsRunnable)
    .flatMap((c) =>
      Array.from({ length: iterations }, (_, i) =>
        models.length
          ? models.map((model) => ({ case: c, iteration: i + 1, model }))
          : [{ case: c, iteration: i + 1 }]
      ).flat()
    );
};

/** The suite's page and customer, as the session reports them. */
export const sessionContextOf = (
  context: SuiteContext | undefined
):
  | {
      pageUrl?: string;
      passthrough?: unknown;
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
    }
  | undefined => {
  if (!context) return undefined;
  const { passthrough, ...rest } = context;
  const text = passthrough?.trim();
  let parsed: unknown = text || undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // Plain text: sent as-is, as the widget accepts the legacy string form.
    }
  }
  return { ...rest, ...(parsed !== undefined ? { passthrough: parsed } : {}) };
};

/**
 * Run `worker` over `items`, at most `limit` at a time — the tester's
 * `processWithConcurrency`. A failed item doesn't stop the rest; its error is
 * its result. Once `signal` aborts, no new item starts.
 */
export const runWithConcurrency = async <T, R>(
  items: T[],
  worker: (item: T, index: number) => Promise<R>,
  {
    limit,
    signal,
    onSettled,
  }: {
    limit: number;
    signal?: AbortSignal;
    onSettled?: (result: PromiseSettledResult<R>, index: number) => void;
  }
): Promise<(PromiseSettledResult<R> | undefined)[]> => {
  const results: (PromiseSettledResult<R> | undefined)[] = new Array(
    items.length
  ).fill(undefined);
  let next = 0;
  const lane = async () => {
    while (next < items.length && !signal?.aborted) {
      const index = next;
      next += 1;
      let result: PromiseSettledResult<R>;
      try {
        // eslint-disable-next-line no-await-in-loop
        result = {
          status: 'fulfilled',
          value: await worker(items[index], index),
        };
      } catch (reason) {
        result = { status: 'rejected', reason };
      }
      results[index] = result;
      onSettled?.(result, index);
    }
  };
  const lanes = Math.max(1, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: lanes }, lane));
  return results;
};

/* ---- Rollups ------------------------------------------------------------ */

export interface SuiteRunSummary {
  /** Runs recorded. */
  total: number;
  scored: number;
  passed: number;
  failed: number;
  /** Runs with no score — the conversation or the judge errored. */
  errored: number;
  /** 0–100, of scored runs; null with nothing scored. */
  passRate: number | null;
  qualityAvg: number | null;
  /** Failed runs first, then errored ones. */
  failures: ValidateRun[];
}

export const summarizeSuiteRun = (runs: ValidateRun[]): SuiteRunSummary => {
  const scored = runs.filter(
    (r): r is ValidateRun & { score: { status: 'scored' } } =>
      r.score.status === 'scored'
  );
  const failed = scored.filter((r) => runPassed(r) === false);
  const errored = runs.filter((r) => r.score.status === 'error');
  return {
    total: runs.length,
    scored: scored.length,
    passed: scored.length - failed.length,
    failed: failed.length,
    errored: errored.length,
    passRate: scored.length
      ? Math.round(((scored.length - failed.length) / scored.length) * 100)
      : null,
    qualityAvg: scored.length
      ? Math.round(
          scored.reduce((sum, r) => sum + qualityScore(r.score.verdict), 0) /
            scored.length
        )
      : null,
    failures: [...failed, ...errored],
  };
};

/** A suite run's own runs, looked up from the runs list, in finish order. */
export const runsOfSuiteRun = (
  suiteRun: SuiteRun,
  runs: ValidateRun[]
): ValidateRun[] => {
  const byId = new Map(runs.map((r) => [r.id, r]));
  return suiteRun.runIds
    .map((id) => byId.get(id))
    .filter((r): r is ValidateRun => r !== undefined);
};

/** The latest run of each suite. */
export const latestSuiteRuns = (
  suiteRuns: SuiteRun[]
): Map<string, SuiteRun> => {
  const latest = new Map<string, SuiteRun>();
  for (const sr of suiteRuns) {
    const seen = latest.get(sr.suiteId);
    if (!seen || sr.startedAt > seen.startedAt) latest.set(sr.suiteId, sr);
  }
  return latest;
};

/* ---- Parsing what storage hands back ------------------------------------ */

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const START: StartPosition[] = ['beginning', 'mid', 'end'];

const parseCase = (v: unknown): SuiteCase | null => {
  if (!isRecord(v) || typeof v.id !== 'string') return null;
  const agent = isRecord(v.expectedAgent) ? v.expectedAgent : null;
  const lines = (x: unknown) =>
    Array.isArray(x)
      ? x.filter((s): s is string => typeof s === 'string' && !!s.trim())
      : [];
  return {
    id: v.id,
    name: typeof v.name === 'string' ? v.name : '',
    description: typeof v.description === 'string' ? v.description : '',
    ...(typeof v.script === 'string' && v.script.trim()
      ? { script: v.script }
      : {}),
    ...(lines(v.mustSay).length ? { mustSay: lines(v.mustSay) } : {}),
    ...(lines(v.mustNotSay).length ? { mustNotSay: lines(v.mustNotSay) } : {}),
    ...(typeof v.expectation === 'string'
      ? { expectation: v.expectation }
      : {}),
    ...(agent && typeof agent.id === 'string' && typeof agent.title === 'string'
      ? { expectedAgent: { id: agent.id, title: agent.title } }
      : {}),
    startAt: START.includes(v.startAt as StartPosition)
      ? (v.startAt as StartPosition)
      : 'beginning',
    maxTurns: clampMaxTurns(v.maxTurns),
  };
};

const CONTEXT_KEYS: (keyof SuiteContext)[] = [
  'pageUrl',
  'passthrough',
  'firstName',
  'lastName',
  'email',
  'phone',
];

const parseContext = (v: unknown): SuiteContext | undefined => {
  if (!isRecord(v)) return undefined;
  const context: SuiteContext = {};
  CONTEXT_KEYS.forEach((k) => {
    if (typeof v[k] === 'string' && (v[k] as string).trim()) {
      context[k] = v[k] as string;
    }
  });
  return Object.keys(context).length ? context : undefined;
};

export const parseSuite = (v: unknown): ValidateSuite | null => {
  if (!isRecord(v) || typeof v.id !== 'string') return null;
  return {
    id: v.id,
    name:
      typeof v.name === 'string' && v.name.trim() ? v.name : 'Untitled suite',
    channelAppId: typeof v.channelAppId === 'string' ? v.channelAppId : null,
    iterations: clampIterations(v.iterations),
    concurrency: clampConcurrency(v.concurrency),
    cases: Array.isArray(v.cases)
      ? v.cases.map(parseCase).filter((c): c is SuiteCase => c !== null)
      : [],
    ...(parseContext(v.context) ? { context: parseContext(v.context) } : {}),
    ...(Array.isArray(v.models)
      ? {
          models: v.models
            .filter((m): m is string => typeof m === 'string' && !!m.trim())
            .slice(0, MAX_MODELS),
        }
      : {}),
    createdAt: typeof v.createdAt === 'number' ? v.createdAt : 0,
    updatedAt: typeof v.updatedAt === 'number' ? v.updatedAt : 0,
  };
};

/**
 * A suite run saved while it was still going never finishes: the page that
 * was running it closed. Mark it stopped rather than show it running forever.
 */
export const parseSuiteRun = (v: unknown): SuiteRun | null => {
  if (
    !isRecord(v) ||
    typeof v.id !== 'string' ||
    typeof v.suiteId !== 'string' ||
    typeof v.startedAt !== 'number' ||
    !Array.isArray(v.runIds)
  ) {
    return null;
  }
  const status: SuiteRunStatus =
    v.status === 'done' || v.status === 'stopped' ? v.status : 'stopped';
  return {
    id: v.id,
    suiteId: v.suiteId,
    suiteName: typeof v.suiteName === 'string' ? v.suiteName : 'Suite',
    channelName: typeof v.channelName === 'string' ? v.channelName : '',
    startedAt: v.startedAt,
    endedAt: typeof v.endedAt === 'number' ? v.endedAt : null,
    status,
    total: typeof v.total === 'number' ? v.total : v.runIds.length,
    runIds: v.runIds.filter((id): id is string => typeof id === 'string'),
    ...(typeof v.stoppedBecause === 'string'
      ? { stoppedBecause: v.stoppedBecause }
      : {}),
  };
};
